import { queryClient } from '@/common/contexts/react-query/ReactQueryProvider';
import {
  cancelReimbursementOrder,
  createCashoutOrder,
  createDonationOrder,
  createOrder,
  createPolicyTopUp,
  createReimbursementOrder,
  deleteOrder,
  getDonationOrderDetail,
  getOrder,
  getOrders,
  getReimbursementOrderDetails,
  updateOrder,
  uploadReimbursementFiles,
} from '@/common/integrations/menhir/employee/orders/orders.service';
import {
  ICancelReimbursementOrderParams,
  ICreateDonationOrderParams,
  ICreateOrderParams,
  ICreatePolicyTopUpParams,
  ICreateReimbursementOrderParams,
  IDeleteOrderParams,
  IDonationOrder,
  IGetOrderParams,
  IGetOrdersParams,
  IGetReimbursementDetailsParams,
  IOrder,
  IOrderOperation,
  IReimbursementOrderDetails,
  IRetirementInvestmentOrder,
  IUpdateOrderParams,
  IUploadReimbursementFilesParams,
  TOrderCategorySlug,
} from '@/common/integrations/menhir/employee/orders/orders.types';
import { MENHIR_ENDPOINTS } from '@/common/integrations/menhir/menhir.endpoints';
import { WithAxiosConfig } from '@/common/types/general';
import { TMoney } from '@coverflex-tech/hypernova-utils';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

export function useGetOrders<
  U extends
    | IOrder<TOrderCategorySlug>
    | IRetirementInvestmentOrder = IOrder<TOrderCategorySlug>,
>(
  { category }: IGetOrdersParams<TOrderCategorySlug>,
  options?: UseQueryOptions<U[]>
) {
  const { data: orders, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.ROOT, category],
    queryFn: () => getOrders<typeof category, U>({ category }),
    ...options,
  });

  return { orders, ...rest };
}

export function useGetOrder<
  U extends
    | IOrder<TOrderCategorySlug>
    | IRetirementInvestmentOrder = IOrder<TOrderCategorySlug>,
>(
  { category, orderId }: IGetOrderParams<TOrderCategorySlug>,
  options?: UseQueryOptions<U>
) {
  const { data: order, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.DETAIL, category, orderId],
    queryFn: () => getOrder<typeof category, U>(category, orderId),
    ...options,
  });

  return { order, ...rest };
}

export const useGetDonationOrderDetail = (
  orderId: string,
  options?: UseQueryOptions<IDonationOrder>
) => {
  const { data: donationOrder, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.DONATIONS.DETAIL, orderId],
    queryFn: () => getDonationOrderDetail(orderId),
    ...options,
  });

  return { donationOrder, ...rest };
};

export const useCreateDonationOrder = (
  options?: UseMutationOptions<IDonationOrder, any, ICreateDonationOrderParams>
) => {
  return useMutation({
    mutationFn: createDonationOrder,
    onSuccess: (data) => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.DETAIL,
        data?.id,
      ]);
    },
    ...options,
  });
};

export const useCreateCashoutOrder = (
  options?: UseMutationOptions<IOrder, any, TMoney>
) => {
  return useMutation({
    mutationFn: createCashoutOrder,
    ...options,
  });
};

export const useCreateOrder = <T extends TOrderCategorySlug>(
  options?: UseMutationOptions<
    string,
    any,
    WithAxiosConfig<ICreateOrderParams<T>>
  >
) => {
  return useMutation({
    mutationFn: ({ axiosConfig, ...param }) => createOrder(param, axiosConfig),
    onSuccess: () => {
      queryClient.invalidateQueries([MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.ROOT]);
    },
    ...options,
  });
};

export const useUpdateOrder = <T extends TOrderCategorySlug>(
  options?: UseMutationOptions<unknown, any, IUpdateOrderParams<T>>
) => {
  return useMutation({
    mutationFn: updateOrder,
    onSuccess: () => {
      queryClient.invalidateQueries([MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.ROOT]);
    },
    ...options,
  });
};

export const useDeleteOrder = <T extends TOrderCategorySlug>(
  options?: UseMutationOptions<unknown, any, IDeleteOrderParams<T>>
) => {
  return useMutation({
    mutationFn: deleteOrder,
    onSuccess: () => {
      queryClient.invalidateQueries([MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.ROOT]);
    },
    ...options,
  });
};

export const useCreatePolicyTopUp = <T extends TOrderCategorySlug>(
  options?: UseMutationOptions<
    IOrderOperation,
    any,
    WithAxiosConfig<ICreatePolicyTopUpParams<T>>
  >
) => {
  return useMutation({
    mutationFn: ({ axiosConfig, ...param }) =>
      createPolicyTopUp(param, axiosConfig),
    onSuccess: () => {
      queryClient.invalidateQueries([MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.ROOT]);
    },
    ...options,
  });
};

export const useGetReimbursementOrderDetails = <T extends TOrderCategorySlug>(
  { category, orderId }: IGetReimbursementDetailsParams<T>,
  options?: UseQueryOptions<IReimbursementOrderDetails<T>>
) => {
  const { data: reimbursementDetails, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.REIMBURSEMENTS.DETAIL,
      category,
      orderId,
    ],
    queryFn: () => getReimbursementOrderDetails({ category, orderId }),
    ...options,
  });

  return { reimbursementDetails, ...rest };
};

export const useCancelReimbursementOrder = () => {
  return useMutation<
    unknown,
    unknown,
    ICancelReimbursementOrderParams<TOrderCategorySlug>
  >({
    mutationFn: cancelReimbursementOrder,
    onSuccess: (_, params) => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.REIMBURSEMENTS.DETAIL,
        params.category,
        params.orderId,
      ]);
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.OPERATIONS.ROOT,
      ]);
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.OPERATIONS.DETAIL,
      ]);
    },
  });
};

export const useCreateReimbursementOrder = <T extends TOrderCategorySlug>() => {
  return useMutation<
    string,
    unknown,
    WithAxiosConfig<
      | ICreateReimbursementOrderParams<T>
      | (Omit<ICreateReimbursementOrderParams<T>, 'files'> & {
          orderId: string;
        })
    >
  >({
    mutationFn: ({ axiosConfig, ...param }) =>
      createReimbursementOrder(param, axiosConfig),
  });
};

export const useUploadReimbursementFiles = <T extends TOrderCategorySlug>() => {
  return useMutation<
    { orderId: string },
    unknown,
    WithAxiosConfig<IUploadReimbursementFilesParams<T>>
  >({
    mutationFn: ({ axiosConfig, ...param }) => uploadReimbursementFiles(param),
  });
};
